import React, { useState, useEffect } from "react";
import "../styles/newCoupon.css";
import { supabase } from "../../supabase";
import { FiStar, FiPlus, FiTrendingUp, FiUsers, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import toast, { Toaster } from 'react-hot-toast';

// A reward is any coupons_catalog row with a pointCost > 0 - redeemed by users
// with points instead of auto-granted for free like a regular coupon (see newCoupon.jsx).
const isReward = (c) => Number(c.pointCost) > 0;

// Maps a coupons_catalog row (snake_case) to the camelCase shape this page
// already renders (matches the old Firestore myCouponsFb doc).
function toCatalogShape(row) {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    pointCost: row.point_cost,
    unlimited: row.unlimited,
    expiration: row.expiration,
    maxUses: row.max_uses,
    priceBadge: row.price_badge,
    deleted: row.deleted,
  };
}

function NewReward() {
  const [rewards, setRewards] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    expiration: "",
    unlimited: false,
    maxUses: 1,
    pointCost: "",
  });
  const [rewardStatsRows, setRewardStatsRows] = useState([]);
  const [allTimeRewardsUsed, setAllTimeRewardsUsed] = useState(0);

  const refreshStats = async (showToast = false) => {
    try {
      const { data: catalogRows, error: catalogError } = await supabase.from('coupons_catalog').select('*');
      if (catalogError) throw catalogError;
      const rewardCoupons = (catalogRows || []).map(toCatalogShape).filter(isReward);

      const rewardIds = rewardCoupons.map(r => r.id);
      const { data: usageRows, error: usageError } = rewardIds.length
        ? await supabase.from('coupon_usage_log').select('coupon_id').in('coupon_id', rewardIds)
        : { data: [] };
      if (usageError) throw usageError;
      const usageCountByCoupon = {};
      (usageRows || []).forEach(r => { usageCountByCoupon[r.coupon_id] = (usageCountByCoupon[r.coupon_id] || 0) + 1; });

      const rows = rewardCoupons.map(c => ({
        id: c.id,
        title: c.title || c.id,
        usedCount: usageCountByCoupon[c.id] || 0,
        isArchived: Boolean(c.deleted),
      }));

      setRewardStatsRows(rows);
      setAllTimeRewardsUsed((usageRows || []).length);

      if (showToast) {
        toast.success('Statistikk oppdatert!', { duration: 2000, position: 'top-center' });
      }
    } catch (err) {
      if (showToast) {
        toast.error('Kunne ikke oppdatere statistikk', { duration: 3000, position: 'top-center' });
      }
    }
  };

  const handleRefreshStats = async () => {
    await refreshStats(true);
  };

  useEffect(() => {
    async function fetchRewards() {
      const { data, error } = await supabase.from('coupons_catalog').select('*');
      if (error) { console.error('Failed to fetch rewards:', error); return; }
      const rewardsList = (data || []).map(toCatalogShape).filter((c) => !c.deleted && isReward(c));
      setRewards(rewardsList);
    }
    fetchRewards();
  }, []);

  useEffect(() => {
    refreshStats(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "unlimited") {
      setForm({
        ...form,
        unlimited: checked,
        expiration: checked ? "" : form.expiration,
      });
    } else if (name === 'maxUses') {
      setForm({ ...form, maxUses: value });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || (!form.expiration && !form.unlimited)) {
      toast.error('Fyll inn tittel, beskrivelse og utløpsdato (eller velg ubegrenset).', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    const normalizedPointCost = Math.max(0, Number.parseInt(form.pointCost, 10) || 0);
    if (normalizedPointCost <= 0) {
      toast.error('Poengpris må være et tall større enn 0.', {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    const normalizedMaxUses = Math.max(1, Number.parseInt(form.maxUses, 10) || 1);

    const newReward = {
      title: form.title,
      description: form.description,
      expiration: form.unlimited ? null : form.expiration,
      unlimited: form.unlimited,
      start_time: '',
      end_time: '',
      max_uses: normalizedMaxUses,
      repeat_weekly: false,
      repeat_days: [],
      point_cost: normalizedPointCost,
    };

    try {
      const { data, error } = await supabase.from('coupons_catalog').insert(newReward).select().single();
      if (error) throw error;

      setRewards([...rewards, toCatalogShape(data)]);
      setForm({
        title: "",
        description: "",
        expiration: "",
        unlimited: false,
        maxUses: 1,
        pointCost: "",
      });

      toast.success('Reward opprettet!', {
        duration: 3000,
        position: 'top-center',
      });
    } catch (error) {
      console.error("Error creating reward: ", error);
      toast.error('Feil ved oppretting av reward', {
        duration: 3000,
        position: 'top-center',
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Ingen utløpsdato";
    const [year, month, day] = dateStr.split("-");
    return `${day}.${month}.${year}`;
  };

  return (
    <>
      <Toaster />
      <AdminDashboardNav />
      <div className="coupon-container">
        {/* Header */}
        <div className="coupon-header">
          <div>
            <h1><FiStar style={{ marginRight: '12px', verticalAlign: 'middle' }} />Reward-administrasjon</h1>
            <p className="coupon-subtitle">Opprett og administrer rewards brukere løser inn med poeng</p>
          </div>
          <div className="coupon-header-stats">
            <div className="coupon-stat-pill">
              <FiStar style={{ fontSize: '14px' }} />
              {rewards.length} Rewards
            </div>
            <div className="coupon-stat-pill">
              <FiUsers style={{ fontSize: '14px' }} />
              {allTimeRewardsUsed} Løst inn
            </div>
          </div>
        </div>

        {/* Create Reward Card */}
        <div className="card coupon-create-card">
          <div className="coupon-create-header">
            <h2><FiPlus style={{ marginRight: '8px', verticalAlign: 'middle' }} />Opprett ny reward</h2>
          </div>

          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Reward-tittel</label>
                <input
                  id="title"
                  name="title"
                  placeholder="Skriv inn reward-tittel"
                  value={form.title}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="expiration">Utløpsdato</label>
                <input
                  id="expiration"
                  name="expiration"
                  type="date"
                  value={form.expiration}
                  onChange={handleChange}
                  className="form-input"
                  disabled={form.unlimited}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Beskrivelse</label>
              <input
                id="description"
                name="description"
                placeholder="Skriv inn reward-beskrivelse"
                value={form.description}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pointCost">Poengpris</label>
                <input
                  id="pointCost"
                  name="pointCost"
                  type="number"
                  min="1"
                  placeholder="f.eks. 1000"
                  value={form.pointCost}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="maxUses">Maks antall bruk per bruker</label>
                <input
                  id="maxUses"
                  name="maxUses"
                  type="number"
                  min="1"
                  value={form.maxUses}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="unlimited"
                checked={form.unlimited}
                onChange={handleChange}
                className="checkbox-input"
              />
              Ubegrenset bruk (ingen utløpsdato)
            </label>

            <button type="submit" className="btn btn-primary btn-create">
              <FiPlus style={{ fontSize: '18px' }} />
              Opprett reward
            </button>
          </form>
        </div>

        {/* Stats Card */}
        <div className="card coupon-stats-card">
          <div className="coupon-stats-header">
            <h2><FiTrendingUp style={{ marginRight: '8px', verticalAlign: 'middle' }} />Bruksstatistikk</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span className="coupon-total-badge">{allTimeRewardsUsed} totalt løst inn</span>
              <button className="refresh-btn" onClick={handleRefreshStats} style={{ background: '#16a34a', fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>
                <FiRefreshCw style={{ marginRight: 0, fontSize: '0.75rem' }} />Oppdater
              </button>
            </div>
          </div>
          <div className="coupon-stats-grid">
            {rewardStatsRows.length === 0 && (
              <div className="coupon-stat-item">
                <div className="coupon-stat-name">Ingen statistikk ennå</div>
                <div className="coupon-stat-value">0 løst inn</div>
              </div>
            )}
            {rewardStatsRows.map((row) => (
              <div key={row.id} className="coupon-stat-item">
                <div className="coupon-stat-name">
                  {row.title}
                  {row.isArchived && (
                    <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#94a3b8' }}>(slettet)</span>
                  )}
                </div>
                <div className="coupon-stat-value">{row.usedCount || 0} løst inn</div>
              </div>
            ))}
          </div>
        </div>

        {/* Rewards List */}
        <div className="card coupons-list-card">
          <div className="coupons-list-header">
            <h2><FiStar style={{ marginRight: '8px', verticalAlign: 'middle' }} />Aktive rewards</h2>
            <span className="coupons-count">{rewards.length} rewards</span>
          </div>

          <div className="coupons-list">
            {rewards.length === 0 && (
              <div className="no-coupons">Ingen rewards opprettet enda.</div>
            )}

            {rewards.map((reward) => (
              <div key={reward.id} className="coupon-list-item">
                <div>
                  <div className="coupon-meta-line">
                    REWARD · {reward.unlimited || reward.expiration === null ? 'UBEGRENSET' : 'AKTIV'}
                  </div>
                  <div className="coupon-list-title">{reward.title}</div>
                  <div className="coupon-list-desc">{reward.description}</div>
                  <div className="coupon-list-extra">
                    {reward.unlimited || reward.expiration === null
                      ? 'Ingen utløpsdato'
                      : reward.expiration && reward.expiration.trim() !== ''
                      ? `Utløper: ${formatDate(reward.expiration)}`
                      : 'Ingen utløpsdato'}
                  </div>
                  <div className="coupon-list-extra">
                    Maks bruk per bruker: {Math.max(1, Number.parseInt(reward.maxUses, 10) || 1)}
                  </div>
                  {reward.priceBadge && (
                    <div className="coupon-list-extra">
                      Prisbadge: {reward.priceBadge}
                    </div>
                  )}
                  <div className="coupon-list-extra">
                    Poengpris: {reward.pointCost} poeng
                  </div>
                </div>
                <div className="coupon-item-actions">
                  <button
                    className="coupon-danger-btn"
                    onClick={async () => {
                      try {
                        const { error } = await supabase.from('coupons_catalog').update({ deleted: true }).eq('id', reward.id);
                        if (error) throw error;
                        setRewards(rewards.filter((r) => r.id !== reward.id));
                        await refreshStats(false);
                        toast.success('Reward slettet!', { duration: 2000, position: 'top-center' });
                      } catch (error) {
                        console.error('Error deleting reward: ', error);
                        toast.error('Feil ved sletting av reward', { duration: 3000, position: 'top-center' });
                      }
                    }}
                  >
                    <FiTrash2 style={{ marginRight: 6, verticalAlign: 'middle' }} />Slett
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default NewReward;
